import { useEffect } from 'react'

import { deepSetObject, digObject, toggleArray } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import type { EntitiesState } from '@redux/entities'

import useScopedEntities from './useScopedEntities'
import type { FilteredEntities } from './useScopedEntities'

const nextStepKeys = {
  providerIds: 'serviceProductIds',
  serviceProductIds: 'agencyIds',
  agencyIds: 'publicationIds',
  publicationIds: 'publicationProductIds',
  publicationProductIds: 'templateGroupIds',
  templateGroupIds: 'brandThemeIds',
}

type PermissionGroup = {
  agencyIds: number[],
  brandThemeIds: number[],
  providerIds: number[],
  publicationIds: number[],
  publicationProductIds: number[],
  serviceProductIds: number[],
  templateGroupIds: number[],
}

export type Permissions = {
  current: PermissionGroup,
  queued: PermissionGroup,
}

const defaultState = {
  activeSegmentKey: 'all',
  activeStepKey: 'publicationIds',
  dirtyEntityKeys: {
    agencyIds: false,
    brandThemeIds: false,
    providerIds: false,
    publicationIds: false,
    publicationProductIds: false,
    serviceProductIds: false,
    templateGroupIds: false,
  },
  filterString: '',
  permissions: {
    current: {
      agencyIds: [],
      brandThemeIds: [],
      providerIds: [],
      publicationIds: [],
      publicationProcutIds: [],
      serviceProductIds: [],
      templateGroupIds: [],
    },
    queued: {
      agencyIds: [],
      brandThemeIds: [],
      providerIds: [],
      publicationIds: [],
      publicationProductIds: [],
      serviceProductIds: [],
      templateGroupIds: [],
    },
  },
}

const updateQueuedEntityIds = (params: {
  entityKey: string,
  entityIds: number[],
  state: typeof defaultState,
  setState: (state: Partial<typeof defaultState>) => void,
}) => {
  const {
    entityIds, entityKey, setState, state,
  } = params

  const updatedQueuedPermissions = deepSetObject(state, `permissions.queued.${entityKey}`, entityIds)
  const updatedDirtyEntityKeys = deepSetObject(updatedQueuedPermissions, `dirtyEntityKeys.${entityKey}`, true)

  setState(updatedDirtyEntityKeys)
}

const bulkDeselectEntityIds = (params: {
  entityKey: string,
  entityIds: number[],
  permissions: Permissions,
  state: typeof defaultState,
  setState: (state: Partial<typeof defaultState>) => void,
}) => {
  const {
    entityIds, entityKey, permissions, setState, state,
  } = params

  const currentEntityIds = permissions.queued[entityKey] ?? []
  const filteredEntityIds = currentEntityIds.filter(id => !entityIds.includes(id))

  updateQueuedEntityIds({
    entityKey,
    entityIds: filteredEntityIds,
    state,
    setState,
  })
}

const bulkSelectEntityIds = (params: {
  entityKey: string,
  entityIds: number[],
  permissions: Permissions,
  state: typeof defaultState,
  setState: (state: Partial<typeof defaultState>) => void,
}) => {
  const {
    entityIds, entityKey, permissions, setState, state,
  } = params

  const currentEntityIds = permissions.queued[entityKey] ?? []

  const mergedIds = [...currentEntityIds, ...entityIds]
  const uniqueIds = Array.from(new Set(mergedIds))

  updateQueuedEntityIds({
    entityKey,
    entityIds: uniqueIds,
    state,
    setState,
  })
}

const filterUnassignedEntityIds = (params: { currentIds: number[], entityArray: object[] }) => {
  const { currentIds, entityArray } = params

  const entityIds = entityArray.map(entity => entity.id)
  const filteredIds = currentIds.filter(id => entityIds.includes(id))

  return filteredIds
}

const filterUnassignedEntities = (params: {
  entityKey: string,
  entities: Partial<EntitiesState>,
  permissions: Permissions,
}) => {
  const { entities, entityKey, permissions } = params

  const currentEntityIds = permissions.current[entityKey] ?? []
  const queuedEntityIds = permissions.queued[entityKey] ?? []

  const unassignedEntityIds = currentEntityIds.filter(id => !queuedEntityIds.includes(id))

  const mapped = unassignedEntityIds.map((id) => {
    const entity = entities[id] ?? { id, title: 'Unassigned Entity' }
    return entity
  })

  return mapped
}

const filterVisibleEntities = (params: {
  activeSegmentKey: string,
  entityKey: string,
  entities: object[],
  permissions: Permissions,
}) => {
  const {
    activeSegmentKey, entities, entityKey, permissions,
  } = params

  if (activeSegmentKey === 'selected'){
    const queuedEntityIds = permissions.queued[entityKey] ?? []
    const filtered = entities.filter((entity: object) => {
      const { id } = entity
      return queuedEntityIds.includes(id)
    })

    return filtered
  }

  return entities
}

type MergeUniqueIdsParams = {
  currentPermissions: {
    [key: string]: number[],
  },
  dirtyEntityKeys: typeof defaultState['dirtyEntityKeys'],
  entityArray: object[],
  entityKey: string,
  permissions: Permissions,
}

const mergeAndFilterUniqueIds = (params: MergeUniqueIdsParams) => {
  const {
    currentPermissions,
    dirtyEntityKeys,
    entityArray,
    entityKey,
    permissions: {
      queued: queuedPermissions,
    },
  } = params

  const isEntityKeyDirty = dirtyEntityKeys[entityKey]
  const queuedPermissionIds = queuedPermissions[entityKey]

  // Only use the current/saved template page permissions
  // if the user hasn't toggled entities
  const mergedIds = isEntityKeyDirty
    ? queuedPermissionIds
    : [...currentPermissions[entityKey], ...queuedPermissionIds]

  // Remove any ids that are not in the filtered array
  // e.g. user deselects a provider that was required
  // for a certain product
  const assignedEntityIds = filterUnassignedEntityIds({
    currentIds: mergedIds,
    entityArray,
  })

  const uniqueIds = new Set(assignedEntityIds)

  return Array.from(uniqueIds)
}

type PreparePermissionsParams = {
  state: typeof defaultState,
}

const preparePermissions = (params: PreparePermissionsParams) => {
  const { state: { dirtyEntityKeys, permissions: { queued: queuedPermissionIds } } } = params

  const mappedPermissions = {
    agency_ids: dirtyEntityKeys.agencyIds ? queuedPermissionIds.agencyIds : undefined,
    brand_theme_ids: dirtyEntityKeys.brandThemeIds ? queuedPermissionIds.brandThemeIds : undefined,
    provider_ids: dirtyEntityKeys.providerIds ? queuedPermissionIds.providerIds : undefined,
    publication_ids: dirtyEntityKeys.publicationIds ? queuedPermissionIds.publicationIds : undefined,
    publication_product_ids: dirtyEntityKeys.publicationProductIds ? queuedPermissionIds.publicationProductIds : undefined,
    service_product_ids: dirtyEntityKeys.serviceProductIds ? queuedPermissionIds.serviceProductIds : undefined,
    template_group_ids: dirtyEntityKeys.templateGroupIds ? queuedPermissionIds.templateGroupIds : undefined,
  }

  return mappedPermissions
}

type SetupPermissionsParams = {
  callbacks: {
    setState: (state: Partial<typeof defaultState>) => void,
  },
  dirtyEntityKeys: typeof defaultState['dirtyEntityKeys'],
  filteredEntities: FilteredEntities,
  entity: unknown,
  permissions: Permissions,
}

const setupPermissions = (params: SetupPermissionsParams) => {
  const {
    callbacks: { setState },
    dirtyEntityKeys,
    filteredEntities: {
      agencies, brandThemes, providers, publications, publicationProducts, serviceProducts, templateGroups,
    },
    permissions,
    entity,
  } = params

  const currentPermissions = {
    agencyIds: digObject(entity, 'permission_ids.agencies', []),
    brandThemeIds: digObject(entity, 'permission_ids.brand_themes', []),
    providerIds: digObject(entity, 'permission_ids.providers', []),
    publicationIds: digObject(entity, 'permission_ids.publications', []),
    publicationProductIds: digObject(entity, 'compatible_products', []),
    serviceProductIds: digObject(entity, 'permission_ids.products', []),
    templateGroupIds: digObject(entity, 'permission_ids.template_groups', []),
  }

  const queuedPermissions = {
    agencyIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: agencies, entityKey: 'agencyIds', permissions,
    }),
    brandThemeIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: brandThemes, entityKey: 'brandThemeIds', permissions,
    }),
    providerIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: providers, entityKey: 'providerIds', permissions,
    }),
    publicationIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: publications, entityKey: 'publicationIds', permissions,
    }),
    publicationProductIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: publicationProducts, entityKey: 'publicationProductIds', permissions,
    }),
    serviceProductIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: serviceProducts, entityKey: 'serviceProductIds', permissions,
    }),
    templateGroupIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: templateGroups, entityKey: 'templateGroupIds', permissions,
    }),
  }

  const updatedState = {
    permissions: {
      current: currentPermissions,
      queued: queuedPermissions,
    },
  }

  setState(updatedState)
}

type UseAssignEntityPermissionsModal = {
  activeStepKey?: string,
  customStepKeys?: {
    [key: string]: string,
  },
  entity: unknown,
}

function useAssignEntityPermissionsModal(options: UseAssignEntityPermissionsModal) {
  const {
    activeStepKey: initActiveStepKey, customStepKeys, entity,
  } = options

  const brandId = digObject(entity, 'brand_id', null)
  const serviceId = digObject(entity, 'service_id', null)
  const cacheKey = digObject(entity, 'cache_key', '')
  const permissionIds = digObject(entity, 'permission_ids', {})

  const [state, setState] = useSetState(defaultState)
  const {
    activeSegmentKey,
    activeStepKey,
    dirtyEntityKeys,
    filterString,
    permissions,
    permissions: {
      queued: queuedPermissionIds,
    },
  } = state

  useEffect(() => {
    if (initActiveStepKey && permissionIds?.providers?.length){
      setState({ activeStepKey: initActiveStepKey })
    }
  }, [initActiveStepKey])

  const scopedEntityPayload = useScopedEntities({
    activeStepKey,
    filters: {
      brandId,
      serviceId,
      string: filterString,
    },
    permissions,
    entity,
  })

  const { entitiesUpdatedAt, filteredEntities, payloads } = scopedEntityPayload

  useEffect(() => {
    if (entity?.id){
      setupPermissions({
        callbacks: {
          setState,
        },
        dirtyEntityKeys,
        filteredEntities,
        permissions,
        entity,
      })
    }
  }, [activeStepKey, entity?.id, cacheKey, entitiesUpdatedAt])

  const nextStepKey = Object.values(customStepKeys).length ? customStepKeys[activeStepKey as keyof typeof customStepKeys] : nextStepKeys[activeStepKey as keyof typeof nextStepKeys]

  const hasNextStep = !!nextStepKey

  const saveEnabled = !!queuedPermissionIds.providerIds.length
    && !!queuedPermissionIds.serviceProductIds.length
    && !!queuedPermissionIds.agencyIds.length

  return {
    activeSegmentKey,
    activeStepKey,
    callbacks: {
      bulkDeselectEntityIds: (params: { entityKey: string, entityIds: number[] }) => {
        const { entityKey, entityIds } = params

        return bulkDeselectEntityIds({
          entityIds,
          entityKey,
          permissions,
          setState,
          state,
        })
      },
      bulkSelectEntityIds: (params: { entityKey: string, entityIds: number[] }) => {
        const { entityIds, entityKey } = params

        return bulkSelectEntityIds({
          entityIds,
          entityKey,
          permissions,
          setState,
          state,
        })
      },
      filterUnassignedEntities: (params: {
        entityKey: string,
        entities: Partial<EntitiesState>,
      }) => {
        const { entityKey, entities } = params

        return filterUnassignedEntities({
          entities,
          entityKey,
          permissions,
        })
      },
      filterVisibleEntities: (params: {
        entityKey: string,
        entities: object[],
      }) => {
        const { entities, entityKey } = params

        return filterVisibleEntities({
          activeSegmentKey,
          entityKey,
          entities,
          permissions,
        })
      },
      preparePermissions: () => (
        preparePermissions({
          state,
        })
      ),
      setCurrentSegmentKey: (key: string) => setState({ activeSegmentKey: key, filterString: '' }),
      setCurrentStepKey: (key: string) => setState({ activeStepKey: key }),
      setState,
      toggleEntityId: (params: { entityKey: string, entityId: number }) => {
        const { entityId, entityKey } = params

        const updatedEntityIds = toggleArray(permissions.queued[entityKey], entityId)

        updateQueuedEntityIds({
          entityKey,
          entityIds: updatedEntityIds,
          state,
          setState,
        })
      },
    },
    filterString,
    hasNextStep,
    nextStepKey,
    payloads,
    permissions,
    saveEnabled,
    entity,
  }
}

export default useAssignEntityPermissionsModal
